/**
 * THE SCOPED QUEUE (unified resolution surface §10) — the guided walk's queue is
 * the DERIVED WORKLIST FILTERED BY A URL SCOPE. Nothing here derives state and
 * nothing here is stored: `filterQueue` returns the very issue objects
 * `deriveWorklist` produced, in their own order, with the out-of-scope ones
 * dropped. `scope=all` is the identity filter, which is why the unscoped queue is
 * byte-identical to what it was before this existed.
 *
 *   entry                        scope            queue
 *   mission control              all              every issue, worklist order
 *   an article's ⚑ badge         article:<id>     every issue touching it —
 *                                                 ACROSS frameworks and steps
 *   a statement card's ⚑ badge   statement:<id>   that statement's issues
 *
 * **WHICH ARTICLES AN ISSUE TOUCHES** is not on `WorklistIssue` (steps 1 and 2
 * carry no article list), so it rides beside the worklist as `IssueArticles`,
 * derived on the same pass. Keeping it off the issue is deliberate: the
 * worklist's own bytes never move for this feature — they are pinned, byte for
 * byte, against the Python twin by `threeStateParity.test.ts`.
 *
 * Pure and client-safe. Ported from the studio `src/core/queueScope.ts`; the one
 * adaptation is that the unconfirmed SWEEP (§9, a later build) has no member
 * narrowing here because this app has no sweep unit yet.
 */
import { issueId, type StateContext, type WorklistIssue } from "./cellState"

/** The queue's scope, straight off `&scope=` (§13). */
export type QueueScope =
  | { kind: "all" }
  | { kind: "article"; id: string }
  | { kind: "statement"; id: string }

/** The whole worklist — the identity filter, and the default everywhere. */
export const ALL_SCOPE: QueueScope = { kind: "all" }

/** Issue key (`${step}:${statementId}`) → the articles that issue touches. */
export type IssueArticles = Record<string, string[]>

/**
 * Read `&scope=`. Tolerant by design: an unknown, malformed or absent value is
 * the unscoped queue, which is the behaviour that existed before the param. A
 * scope naming something that no longer exists yields an EMPTY queue, not a
 * broken one — the rail then offers its way back to the workspace.
 */
export function parseScope(raw: string | null | undefined): QueueScope {
  if (!raw || raw === "all") return ALL_SCOPE
  const at = raw.indexOf(":")
  if (at <= 0) return ALL_SCOPE
  const kind = raw.slice(0, at)
  const id = raw.slice(at + 1)
  if (!id) return ALL_SCOPE
  if (kind === "article") return { kind: "article", id }
  if (kind === "statement") return { kind: "statement", id }
  return ALL_SCOPE
}

/** The param value, or null for the unscoped queue (which carries no param). */
export function serializeScope(scope: QueueScope): string | null {
  return scope.kind === "all" ? null : `${scope.kind}:${scope.id}`
}

/** The articles one issue touches, from the map beside the worklist. */
export function articlesOfIssue(
  map: IssueArticles | undefined,
  i: WorklistIssue,
): string[] {
  return map?.[issueId(i.step, i.unitId)] ?? []
}

/** Is one issue in scope? A statement scope reads the issue's own unit; an
 *  article scope reads the map. */
export function issueInScope(
  i: WorklistIssue,
  scope: QueueScope,
  map?: IssueArticles,
): boolean {
  if (scope.kind === "all") return true
  if (scope.kind === "statement") return i.unitId === scope.id
  return articlesOfIssue(map, i).includes(scope.id)
}

/** THE QUEUE: the derived worklist, filtered. Same objects, same order. */
export function filterQueue(
  worklist: WorklistIssue[],
  scope: QueueScope,
  map?: IssueArticles,
): WorklistIssue[] {
  if (scope.kind === "all") return worklist
  return worklist.filter((i) => issueInScope(i, scope, map))
}

/**
 * The articles every issue touches, keyed by issue id — the article scope's
 * whole input. A step-3 issue names its own open articles; steps 1 and 2 carry
 * none of their own, so they inherit the statement's scope, which is exactly the
 * set an article badge means by "issues touching me".
 */
export function deriveIssueArticles(
  ctx: StateContext,
  worklist: WorklistIssue[],
): IssueArticles {
  const scopeOf = new Map(ctx.statements.map((s) => [s.id, s.selectedArticles]))
  const out: IssueArticles = {}
  for (const i of worklist) {
    out[issueId(i.step, i.unitId)] = i.articleIds.length
      ? i.articleIds
      : (scopeOf.get(i.unitId) ?? [])
  }
  return out
}

/** The item this entry addresses inside the issue it opens (`&item=`), when the
 *  scope names one. An article scope does; a statement scope's item IS the unit. */
export function scopeItemKey(scope: QueueScope): string | null {
  return scope.kind === "article" ? `art:${scope.id}` : null
}

// ── THE FLAG BADGE (§10) ─────────────────────────────────────────────────────

/**
 * One line of a badge's tooltip, as data: the step's verb and the framework. The
 * point of the enumeration is that it names the FRAMEWORK, because that is the
 * axis a statement-first surface hides — two frameworks' verify work on one
 * article looks like nothing at all until the article's own row says so. The
 * component turns it into words; the three translation files own the verbs.
 */
export interface FlagLine {
  step: 1 | 2 | 3
  framework: string
}

export function flagLines(issues: WorklistIssue[]): FlagLine[] {
  return issues.map((i) => ({ step: i.step, framework: i.framework }))
}

/** The scoped queue for one article / one statement — what its badge counts and
 *  what its click opens. A row with none renders no badge at all (grey means
 *  blocked; an absent badge means nothing is open). */
export function articleQueue(
  worklist: WorklistIssue[],
  articleId: string,
  map?: IssueArticles,
): WorklistIssue[] {
  return filterQueue(worklist, { kind: "article", id: articleId }, map)
}

export function statementQueue(
  worklist: WorklistIssue[],
  statementId: string,
): WorklistIssue[] {
  return filterQueue(worklist, { kind: "statement", id: statementId })
}
